import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClinet from "../../api/Api.js";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit"; // Import Edit Icon

// Fetch decline reasons
const fetchDeclineReasons = async () => {
  const response = await apiClinet.get(
    `${process.env.REACT_APP_NEW_DATA}/getDeclines`
  );
  return response.data.declines;
};

// Add new decline reason
const addDeclineReason = async (newReason) => {
  await apiClinet.post(`${process.env.REACT_APP_NEW_DATA}/adddeclinereason`, {
    name: newReason,
  });
};

// Delete decline reason
const deleteDeclineReason = async (decline_id) => {
  await apiClinet.delete(
    `${process.env.REACT_APP_NEW_DATA}/deletedecline/${decline_id}`
  );
};

// Edit/update decline reason
const editDeclineReason = async ({ declineId, updatedName }) => {
  const decline_id = declineId;
  await apiClinet.put(
    `${process.env.REACT_APP_NEW_DATA}/updatedecline/${decline_id}`,
    { name: updatedName }
  );
};

export default function DeclineReasons() {
  const [newReason, setNewReason] = useState("");
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, error, isLoading } = useQuery({
    queryKey: ["declineReasons"],
    queryFn: fetchDeclineReasons,
  });

  const addMutation = useMutation({
    mutationFn: addDeclineReason,
    onSuccess: () => {
      queryClient.invalidateQueries(["declineReasons"]);
      setNewReason("");
      handleClose();
    },
    onError: (error) => {
      console.error("Error adding decline reason:", error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDeclineReason,
    onSuccess: () => {
      queryClient.invalidateQueries(["declineReasons"]);
    },
    onError: (error) => {
      console.error("Error deleting decline reason:", error);
    },
  });

  const editMutation = useMutation({
    mutationFn: editDeclineReason,
    onSuccess: () => {
      queryClient.invalidateQueries(["declineReasons"]);
      setNewReason("");
      setEditMode(false);
      setCurrentEditId(null);
      handleClose();
    },
    onError: (error) => {
      console.error("Error editing decline reason:", error);
    },
  });

  const handleAddDecline = () => {
    if (newReason.trim()) {
      if (editMode) {
        editMutation.mutate({
          declineId: currentEditId,
          updatedName: newReason,
        });
      } else {
        addMutation.mutate(newReason);
      }
    }
  };

  const handleDeleteDecline = (declineId) => () => {
    if (
      window.confirm("Are you sure you want to delete this decline reason?")
    ) {
      deleteMutation.mutate(declineId);
    }
  };

  const handleEditDecline = (reason) => () => {
    setNewReason(reason.name);
    setCurrentEditId(reason.decline_id);
    setEditMode(true);
    setOpen(true);
  };

  const handleClickOpen = () => {
    setNewReason("");
    setEditMode(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
    setNewReason("");
    setCurrentEditId(null);
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <>
      {/* Add Decline Reason Button */}
      <Box
        sx={{
          position: "absolute",
          top: 20,
          right: isMobile ? 10 : 20,
        }}
      >
        <Button variant="contained" color="primary" onClick={handleClickOpen}>
          Add Decline
        </Button>
      </Box>

      {/* Decline Reasons Table */}
      <TableContainer
        component={Paper}
        style={{ marginTop: "80px" }}
        sx={{
          marginX: isMobile ? 1 : 5,
        }}
      >
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell>Decline ID</TableCell>
              <TableCell>Decline Reason</TableCell>
              <TableCell>Edit</TableCell>
              <TableCell>Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((reason) => (
                <TableRow key={reason.decline_id}>
                  <TableCell>{reason.decline_id}</TableCell>
                  <TableCell>{reason.name}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={handleEditDecline(reason)}
                      sx={{ color: "blue" }}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={handleDeleteDecline(reason.decline_id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for Add/Edit */}
      <Dialog fullScreen={isMobile} open={open} onClose={handleClose}>
        <DialogTitle>
          {editMode ? "Edit Decline Reason" : "Add Decline Reason"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Decline Reason"
            type="text"
            fullWidth
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddDecline} color="primary">
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
